// src/pages/traspaso_historico_consultas.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import 'animate.css';
// OJO: ya tienes la hoja CSS global de fontawesome importada en tu app,
// no hace falta importarla aquí otra vez.

import { resolveApiBaseList } from '../utils/apiBase';


const ENTIDADES = [
  { key: 'alumnos',  icon: 'fa-user-graduate',   label: 'Alumnos' },
  { key: 'docentes', icon: 'fa-chalkboard-teacher', label: 'Docentes' },
  { key: 'materias', icon: 'fa-book',            label: 'Materias' },
  { key: 'horarios', icon: 'fa-clock',           label: 'Horarios' },
  { key: 'calificaciones', icon: 'fa-clipboard-check', label: 'Calificaciones' },
];

export default function ConsultarTraspaso() {
  const [entity, setEntity] = useState('alumnos');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchWithFallback = useCallback(async (path, options = {}, parse = 'json') => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const resp = await fetch(url, options);
        if (!resp.ok) {
          let message = `${resp.status}`;
          try { const text = await resp.text(); message = text || message; } catch {}
          lastError = new Error(message);
          lastError.status = resp.status;
          console.warn('Warning: petición fallida a', url, lastError);
          continue;
        }
        if (parse === 'blob') return { data: await resp.blob(), response: resp };
        if (parse === 'text') return { data: await resp.text(), response: resp };
        const text = await resp.text();
        if (!text.trim()) return { data: {}, response: resp };
        try { return { data: JSON.parse(text), response: resp }; } catch (e) {
          lastError = e; console.warn('JSON parse error', url, e); continue;
        }
      } catch (err) {
        lastError = err;
        console.warn('Network error for', url, err);
      }
    }
    throw lastError || new Error('No se pudo contactar al servidor');
  }, [apiBases]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Buscar
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        qs.set('entity', entity);
        qs.set('q', debounced);
        qs.set('page', String(page));
        qs.set('limit', String(limit));
        const path = `/historico/search?${qs.toString()}`;

        const { data } = await fetchWithFallback(path, { method: 'GET' });
        if (cancel) return;
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setTotal(Number(data.total || 0));
      } catch (e) {
        if (!cancel) {
          console.error('Buscar histórico:', e);
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [entity, debounced, page, limit]);

  useEffect(() => { setPage(1); }, [entity]);
  useEffect(() => { setPage(1); }, [debounced]);

  const renderTable = () => {
    if (loading) return <div className="text-muted py-4 text-center">Buscando…</div>;
    if (!rows.length) return <div className="text-muted py-4 text-center">Sin resultados</div>;

    if (entity === 'alumnos') {
      return (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th><th>Nombre</th><th>CURP</th><th>Correo</th><th>Sangre</th><th>Teléfono</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.IdAlumno}>
                  <td>{r.IdAlumno}</td>
                  <td>{r.NombreCompleto}</td>
                  <td>{r.CURP}</td>
                  <td>{r.Correo || '-'}</td>
                  <td>{r.TipoSangre || '-'}</td>
                  <td>{r.Telefono || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entity === 'docentes') {
      return (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th><th>Nombre</th><th>Puesto</th><th>Correo</th><th>Teléfono</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.IdDocente}>
                  <td>{r.IdDocente}</td>
                  <td>{r.NombreCompleto}</td>
                  <td>{r.Puesto || '-'}</td>
                  <td>{r.Correo || '-'}</td>
                  <td>{r.Telefono || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entity === 'materias') {
      return (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th><th>Materia</th><th>Categoría</th><th>Grado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.IdMateria}>
                  <td>{r.IdMateria}</td>
                  <td>{r.Nombre}</td>
                  <td>{r.Categoria}</td>
                  <td>{r.GradoAsignado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entity === 'horarios') {
      return (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th><th>Grupo</th><th>Grado</th><th>Ciclo</th><th>Materia</th><th>Docente</th><th>Día</th><th>Inicio</th><th>Fin</th><th>Aula</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.IdHorario}>
                  <td>{r.IdHorario}</td>
                  <td>{r.IdGrupo}</td>
                  <td>{r.Grado}</td>
                  <td>{r.CicloEscolar}</td>
                  <td>{r.Materia}</td>
                  <td>{r.Docente}</td>
                  <td>{r.DiaSemana}</td>
                  <td>{r.HoraInicio}</td>
                  <td>{r.HoraFin}</td>
                  <td>{r.Aula || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // calificaciones
    return (
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>ID Lista</th><th>Alumno</th><th>Fase</th><th>Trimestre</th><th>Materia</th><th>Docente</th><th>Calificación</th><th>Asist.</th><th>Inasist.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={`${r.IdCalificaciones}-${r.IdDocente}-${r.NombreMateria}`}>
                <td>{r.IdCalificaciones}</td>
                <td>{r.Alumno}</td>
                <td>{r.Fase}</td>
                <td>{r.Trimeste}</td>
                <td>{r.NombreMateria}</td>
                <td>{r.Docente}</td>
                <td>{r.Calificacion ?? '-'}</td>
                <td>{r.Asistencia ?? '-'}</td>
                <td>{r.Inasistencia ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container bg-white p-4 rounded shadow animate__animated animate__fadeIn">
      <h3 className="mb-3 text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,.35)' }}>
        <i className="fa fa-scroll me-2" /> Consulta de Traspasos Históricos
      </h3>

      {/* Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {ENTIDADES.map(e => (
          <button
            key={e.key}
            className={`btn ${entity === e.key ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setEntity(e.key)}
          >
            <i className={`fa ${e.icon} me-2`} />
            {e.label}
          </button>
        ))}
      </div>

      {/* Search box */}
      <div className="mb-3">
        <label className="form-label">Buscar</label>
        <div className="input-group">
          <input
            className="form-control"
            placeholder="Nombre / texto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="input-group-text">
            <i className="fa fa-search" />
          </span>
        </div>
        <small className="text-muted">
          Busca por nombre, apellidos, CURP, usuario, materia, docente, ciclo, etc. (según la pestaña).
        </small>
      </div>

      {/* Results */}
      <div className="border rounded p-3 bg-light">
        {renderTable()}
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          Página {page} de {pages} • {total} resultado(s)
        </div>
        <div className="btn-group">
          <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
            « Anterior
          </button>
          <button className="btn btn-outline-secondary" disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>
            Siguiente »
          </button>
        </div>
      </div>
    </div>
  );
}
